<template>
    <div style="height:100%" class="white">
        <table class="report-table white">

            <thead class="report-header">
                <tr>
                    <th class="print-only">
                        <!-- will print at the top of every page -->
                        <!-- this is a placeholder component - it is only here to give spacing on every printed page so that the actual site header has space - see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a -->
                        &nbsp;
                    </th>
                </tr>
            </thead>

            <tbody>
                <tr>
                    <td>
                        <v-progress-linear color="primary" indeterminate v-if="loading"></v-progress-linear>
                        <v-container v-else>
                            <v-row>
                                <v-col class="text-center">
                                    <span class="font-weight-bold text-h4">VENOM LOT {{ lot.Barcode }}</span>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Created:</span> {{ lot.CreatedTime | formatDate }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">By:</span> {{ lot.CreatedByEmail | formatUserName(lot.CreatedByFullName, "") }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Closed:</span> {{ lot.ClosedTime | formatDate }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">By:</span> {{ lot.ClosedByEmail | formatUserName(lot.ClosedByFullName, "") }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Species:</span> {{ lot.SpeciesFullName }}
                                </v-col>
                                <v-col cols="6">
                                    <span class="font-weight-bold">Total Weight:</span> {{ lot.TotalWeight || 0 }}g
                                </v-col>

                                <v-col cols="12">
                                    <span class="font-weight-bold">Lot Numbers:</span>
                                    <p>{{ lot.QALotNumbers }}</p>
                                </v-col>
                                <v-col cols="12">
                                    <span class="font-weight-bold">Colour / Appearance:</span>
                                    <p>{{ lot.QAAppearance }}</p>
                                </v-col>
                                <v-col cols="12">
                                    <span class="font-weight-bold">RP-HPLC STD. PROTOCOL ANALYSIS:</span>
                                    <p>{{ lot.QAProtocolAnalysis }}</p>
                                </v-col>
                                <v-col cols="12">
                                    <span class="font-weight-bold">Result:</span>
                                    <p>{{ lot.QAResult }}</p>
                                </v-col>
                                <v-col cols="12">
                                    <span class="font-weight-bold">Quality:</span>
                                    <p>{{ lot.QAQuality }}</p>
                                </v-col>
                                <v-col cols="12">
                                    <span class="font-weight-bold">Comments:</span>
                                    <p>{{ lot.QAComments }}</p>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <span class="font-weight-bold text-h6">Species Batch Pools in Lot</span>
                                    <v-simple-table>
                                        <template v-slot:default>
                                            <thead>
                                                <tr>
                                                    <th class="text-left">
                                                        Barcode
                                                    </th>
                                                    <th class="text-left">
                                                        Species
                                                    </th>
                                                    <th class="text-left">
                                                        Weight
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody v-if="lot.SpeciesBatchPools.length !== 0">
                                                <tr v-for="p in lot.SpeciesBatchPools" :key="p.SpeciesBatchPoolID">
                                                    <td>{{ p.Barcode }}</td>
                                                    <td>{{ p.SpeciesFullName }}</td>
                                                    <td>{{ p.TotalWeightAddedToThisVenomLot + "g" }}</td>
                                                </tr>
                                            </tbody>
                                            <tbody v-else>
                                                <tr>
                                                    <td colspan="5">
                                                        There are no species batch pools in this lot
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <span class="font-weight-bold text-h6">Venom Analysis</span>
                                    <PrintHistoricalAnalysis :venomAnalysis="venomAnalysis"></PrintHistoricalAnalysis>
                                </v-col>
                            </v-row>
                        </v-container>
                    </td>
                </tr>
            </tbody>

            <tfoot class="report-footer">
                <tr>
                    <td class="print-only">
                        <!-- will print at the bottom of every page -->
                        <!-- this is a placeholder component - it is only here to give spacing on every printed page so that the actual site footer has space - see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a -->
                        &nbsp;
                    </td>
                </tr>
            </tfoot>

            <!-- this will be fixed to the bottom of the page, and repeated on each printed page; to achieve the proper spacing, see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a -->
            <div class="print-footer-container print-only">
                Report generated by {{name}} at {{$moment().format($constants.DATE_FORMAT)}}
            </div>

        </table>
    </div>
</template>

<script>
import util from '@/helpers/util';
import moment from "moment";
import constants from "comunet-btg-utah-repository-shared/constants";
import * as venomPoolingAPI from "@/services/venomPoolingAPI";
import * as venomAnalysisAPI from '@/services/venomAnalysisAPI';

import PrintHistoricalAnalysis from '@/components/venomanalysis/PrintHistoricalAnalysis.vue';

export default {
    components: {
        PrintHistoricalAnalysis
    },
    props: {
        venomLotID: {
            type: [ Number, String ],
            default: null
        }
    },
    data() {
        return {
            loading: false,
            venomAnalysis: [],
            lot: {}
        }
    },
    computed: {
        name: function () {
            return util.getUserProfileNameFromUserObject(this.$store.state.authStore.user);
        }
    },
    async created() {
        this.loading = true;

        this.lot = await venomPoolingAPI.getVenomLots({ venomLotID: this.venomLotID });
        this.venomAnalysis = await venomAnalysisAPI.getHistoricalAnalysis(-1, -1, -1, this.venomLotID);

        this.loading = false;

        this.$nextTick(() => window.print());
    },
    filters: {
        formatDate: function (value) {
            if (!value) {
                return "";
            }

            var date = moment(value);
            if (date.isValid) {
                return date.format(constants.DATE_FORMAT);
            } else {
                return value;
            }
        },
        formatUserName(email, firstname, surname) {
            if (!email) {
                return "";
            }

            var desc = "";
            if (firstname && firstname != "") {
                desc = firstname;
            }

            if (surname && surname != "") {
                desc += (desc == "" ? "" : " ") + surname;
            }

            desc += (desc == "" ? "" : " (") + email + (desc == "" ? "" : ")");

            return desc;
        },
    }
}
</script>

<style lang="scss">
#app {
    .report-table {
        width: 100%;
    }

    // this is a placeholder - if it is needed then we'll need to change the height
    .report-header th {
        height: 0px;
    }

    .report-footer td, .print-footer-container {
        height: 50px; // this reserves space for the actual footer, see: https://medium.com/@Idan_Co/the-ultimate-print-html-template-with-header-footer-568f415f6d2a
    }

    .print-footer-container {
        position: fixed;
        bottom: 0px;
        width: 100%;
        text-align: center;
        color: var(--v-lightgray-base);
    }
}

@media print {
    #app {
        // shade every second row
        .v-data-table__wrapper table tr:nth-child(2n) {
            background-color: var(--v-lightgray-lighten1)
        }
    }
}
</style>